import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const IMG = 'https://images.unsplash.com/photo-1560279966-2d681f3d4dfc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

export default function HydropowerUseCasePage() {
  return (
    <div>
      <section className="public-hero-wash">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Badge variant="secondary">Use case</Badge>
            <h1 className="font-display text-4xl md:text-5xl font-semibold mt-3">Hydropower & dam infrastructure.</h1>
            <p className="mt-4 text-muted-foreground max-w-xl">Aakash Drishti monitors reservoirs, dam structures, powerhouses, spillways, catchment slopes, downstream zones and approach roads — continuously flagging changes that matter to operations, safety and asset integrity.</p>
            <Link to="/request-demo">
              <Button size="lg" className="mt-6" data-testid="hydropower-demo-cta">Request a pilot</Button>
            </Link>
          </div>
          <img src={IMG} alt="Hydropower dam" className="rounded-2xl border border-border shadow-lg h-[420px] object-cover w-full" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <h2 className="font-display text-3xl font-semibold">What we monitor.</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {[
            { t: 'Reservoir & shoreline', d: 'Water spread, drawdown, unexpected inundation, and shoreline retreat near critical structures.' },
            { t: 'Slopes & landslides', d: 'Fresh soil exposure and debris flows on catchment and access-road slopes.' },
            { t: 'Access & downstream', d: 'Road obstruction, culvert washouts, flood extent affecting settlements.' },
            { t: 'Encroachment', d: 'Unauthorised construction and development inside protection and restricted zones.' },
            { t: 'Fire & burn scars', d: 'Wildfires in the catchment threatening water quality and sedimentation.' },
            { t: 'Storage & yards', d: 'Material stockpiles, switchyard changes, colony expansion.' },
          ].map((x) => (
            <Card key={x.t} className="p-5 border-border">
              <div className="font-semibold">{x.t}</div>
              <div className="text-sm text-muted-foreground mt-1">{x.d}</div>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
          <h2 className="font-display text-3xl font-semibold">Zones tailored to hydropower.</h2>
          <p className="text-muted-foreground mt-2">Seven zone types — <b>core</b>, <b>protection</b>, <b>catchment</b>, <b>downstream</b>, <b>access</b>, <b>slope</b>, and <b>restricted development</b> — each with independent risk sensitivity and alert thresholds.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <h2 className="font-display text-3xl font-semibold">Limitations & disclaimers.</h2>
        <ul className="mt-4 space-y-2 text-sm text-muted-foreground list-disc pl-6 max-w-3xl">
          <li>Satellite imagery is not continuous live video; frequency depends on satellite coverage.</li>
          <li>Optical imagery may be affected by cloud, haze, shadow and weather.</li>
          <li>AI detections are probabilistic and require analyst / field verification per organisational SOP.</li>
          <li>The platform does not replace statutory dam-safety inspection, SCADA, IoT or physical surveillance.</li>
          <li>The platform does not provide emergency-action authority.</li>
        </ul>
      </section>
    </div>
  );
}
